import { hasPermission } from '../../../api/src/auth/permissions';
import { ActivityTracker } from '../activity/ActivityTracker';
import { Icon } from '@iconify/react';
import { ROLE_LABELS } from '../../../api/src/auth/access-role';
import {
  Avatar,
  Chip,
  Button,
  Divider,
  Drawer,
  IconButton,
  Tooltip,
  TextField,
  MenuItem,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { useState } from 'react';
import { NavLink, Outlet } from 'react-router';
import { ThemeModeButton } from '../theme-mode';
import { useWorkspace } from '../workspace-context';
import { useSections } from '../section-context';
import { SectionRefreshNotice } from './SectionRefreshNotice';
import { Box, Stack, Typography } from './mui-system';

const drawerWidth = 224;

export function DashboardShell({ onLogout }: { onLogout(): Promise<void> }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, workspaces, activeWorkspace, selectWorkspace } = useWorkspace();
  const { visibleSections: visible } = useSections();
  const visibleSections = visible.filter((section) => hasPermission(user, `${section.id}.view`));
  const navigationItems = user.isAdmin
    ? [
        ...visibleSections,
        { path: '/admin', label: 'Админка', icon: 'solar:shield-user-bold-duotone' },
      ]
    : visibleSections;
  const drawer = (
    <Stack
      height="100%"
      sx={{
        bgcolor: (theme: Theme) =>
          theme.palette.mode === 'dark'
            ? theme.palette.background.default
            : theme.palette.common.white,
        color: 'text.primary',
      }}
    >
      <Stack direction="row" alignItems="center" gap={1.2} px={2} height={64}>
        <Box component="img" src="/logo.svg" alt="" width={34} height={34} />
        <Box>
          <Typography
            sx={(theme: Theme) => ({ fontWeight: theme.colorTheme === 'graphite' ? 600 : 850 })}
            letterSpacing="-.03em"
          >
            SEO.Dashboard
          </Typography>
          <Typography fontSize={10} sx={{ opacity: 0.52, letterSpacing: '.12em' }}>
            CONTROL ROOM
          </Typography>
        </Box>
      </Stack>
      <Divider />
      <Box px={1.5} pt={2}>
        <TextField
          select
          fullWidth
          size="small"
          label="Воркспейс"
          value={activeWorkspace?.id ?? ''}
          onChange={(event) => selectWorkspace(event.target.value)}
        >
          {workspaces.map((workspace) => (
            <MenuItem key={workspace.id} value={workspace.id}>
              {workspace.name}
              {workspace.isShared ? ' · общий' : ''}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <Box px={1.1} py={1.4}>
        {navigationItems.map((item) => (
          <Button
            key={item.path}
            component={NavLink}
            to={item.path}
            onClick={() => setMobileOpen(false)}
            startIcon={<Icon icon={item.icon} width={21} />}
            sx={(theme) => ({
              justifyContent: 'flex-start',
              width: '100%',
              borderRadius: '7px',
              px: 1.4,
              py: 0.75,
              mb: 0.35,
              color: 'text.secondary',
              '&.active, &.active:hover': {
                bgcolor: theme.colorTheme === 'graphite' ? 'action.selected' : 'primary.main',
                color: theme.colorTheme === 'graphite' ? 'text.primary' : 'primary.contrastText',
              },
              '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
            })}
          >
            {item.label}
          </Button>
        ))}
      </Box>
      <Box mt="auto" p={1.25}>
        <Button component={NavLink} to="/account" onClick={() => setMobileOpen(false)}>
          API-токены
        </Button>
        <Stack gap={1} p={0.8}>
          <Stack direction="row" gap={1} alignItems="center">
            <Avatar src={user.avatarUrl ?? undefined} sx={{ width: 32, height: 32 }}>
              {user.displayName[0]}
            </Avatar>
            <Box minWidth={0} flex={1}>
              <Typography fontSize={13} fontWeight="fontWeightBold" noWrap>
                {user.displayName}
              </Typography>
              <Typography fontSize={11} sx={{ opacity: 0.48 }} noWrap>
                {user.email}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" gap={0.5} alignItems="center" justifyContent="space-between">
            <Tooltip title={ROLE_LABELS[user.role]}>
              <Chip
                label={
                  Object.keys(user.access.overrides).length
                    ? `${ROLE_LABELS[user.role]} · особые права`
                    : user.role === 'viewer'
                      ? 'Только просмотр'
                      : ROLE_LABELS[user.role]
                }
                size="small"
                sx={{
                  height: 20,
                  minWidth: 0,
                  fontSize: 10,
                  color: user.role === 'viewer' ? 'info.main' : 'text.secondary',
                  bgcolor: user.role === 'viewer' ? 'action.selected' : 'action.hover',
                  '& .MuiChip-label': { px: 0.8 },
                }}
              />
            </Tooltip>
            <Stack direction="row" flexShrink={0}>
              <ThemeModeButton />
              <Tooltip title="Выйти">
                <IconButton color="inherit" size="small" onClick={() => void onLogout()}>
                  <Icon icon="solar:logout-2-linear" width={19} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </Stack>
  );

  return (
    <Box minHeight="100vh">
      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { width: drawerWidth, border: 0 },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
      <Box component="main" sx={{ ml: { md: `${drawerWidth}px` }, minHeight: '100vh' }}>
        <Box sx={{ p: { xs: 1.75, sm: 2.5, lg: 3.5 }, maxWidth: 1440, mx: 'auto' }}>
          <IconButton
            aria-label="Открыть меню"
            onClick={() => setMobileOpen(true)}
            sx={{ display: { md: 'none' }, mb: 2, ml: -1 }}
          >
            <Icon icon="solar:hamburger-menu-linear" />
          </IconButton>
          <SectionRefreshNotice />
          <ActivityTracker />
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
